// The permission picker a custom role is built from (F-IAM-11, spec/07 §6.2–§6.3): every permission the
// server assigns, grouped under its resource by the same `groupPermissions` the read-only view uses, each
// a toggle chip. The catalogue is the built-in `owner` role's permissions — the one role that holds them
// all — so the picker offers exactly what the server accepts, without importing the contract's Zod-bearing
// permission list into the browser (the console's `script-src` forbids the `new Function` that would pull
// in, `apps/console/__tests__/pages-csp.test.ts`).

import { useTranslation } from '@clavian/i18n/react';
import { Chip } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { groupPermissions } from './permission-labels';

export function PermissionPicker({
  catalogue,
  selected,
  onToggle,
}: {
  /** Every assignable permission, in any order (the picker groups them). */
  readonly catalogue: readonly string[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (permission: string, on: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <ul className="console-perm-groups" aria-label={t('staff.role.picker.label')}>
      {groupPermissions(catalogue).map((group) => (
        <li key={group.resource} className="console-perm-group">
          <span className="console-perm-group__resource">{group.label}</span>
          <ul className="console-perm-group__actions">
            {group.actions.map((action) => (
              <li key={action.slug}>
                <Chip selected={selected.has(action.slug)} onSelectedChange={(on) => onToggle(action.slug, on)}>
                  {action.label}
                </Chip>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}
